import React, { useCallback, useContext, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faThumbtack } from "@fortawesome/free-solid-svg-icons";
import {
  focusWindow,
  closeWindow,
  minimizeWindow,
  maximizeWindow,
  restoreWindow,
  toggleMaximize,
  moveWindow,
  setWindowBounds,
  WINDOW_STATE,
  MIN_WIDTH,
  MIN_HEIGHT,
} from "../../Slices/windowsSlice";
import { computeStage, detectZone, zoneBounds, SNAP_LAYOUTS } from "./snap";
import WebtopLayoutContext from "../layout/WebtopLayoutContext";
import WebtopWindowContext from "./WebtopWindowContext";
import {
  hasShortcutForPath,
  addShortcut,
  removeShortcutByPath,
  nextShortcutPosition,
} from "../layout/layoutModel";

const isDetailPath = (path) =>
  String(path || "").replace(/^\//, "").split("/").filter(Boolean).length >= 2;

const RESIZE_DIRS = ["n", "s", "e", "w", "ne", "nw", "se", "sw"];

const MinIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <rect x="0" y="4.5" width="10" height="1" fill="currentColor" />
  </svg>
);
const MaxIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <rect x="0.5" y="0.5" width="9" height="9" fill="none" stroke="currentColor" />
  </svg>
);
const RestoreIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <rect x="0.5" y="2.5" width="7" height="7" fill="none" stroke="currentColor" />
    <path d="M2.5 2.5 V0.5 H9.5 V7.5 H7.5" fill="none" stroke="currentColor" />
  </svg>
);
const CloseIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true">
    <path d="M0.5 0.5 L9.5 9.5 M9.5 0.5 L0.5 9.5" stroke="currentColor" strokeWidth="1.1" />
  </svg>
);

const PopoutIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10" aria-hidden="true" fill="none">
    <path d="M4 1H1v8h8V6M6.5 1H9v2.5M9 1L5 5" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" />
  </svg>
);

const Window = React.memo(({ win, children }) => {
  const dispatch = useDispatch();
  // Subscribe only to THIS window's focus state — focusing one window does NOT
  // re-render every other window's chrome.
  const isFocused = useSelector((s) => s.windows.focusedId === win.id);
  const { layout, updateLayout } = useContext(WebtopLayoutContext);
  const canPin = isDetailPath(win.path);
  const isPinnedRecord = canPin && hasShortcutForPath(layout, win.path);

  const togglePinRecord = () => {
    if (isPinnedRecord) {
      updateLayout({ recordShortcuts: removeShortcutByPath(layout, win.path).recordShortcuts });
    } else {
      const shortcut = {
        id: `sc-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        path: win.path,
        label: win.title,
        type: win.path.replace(/^\//, "").split("/")[0],
        ...nextShortcutPosition(layout),
      };
      updateLayout({ recordShortcuts: addShortcut(layout, shortcut).recordShortcuts });
    }
  };

  const isMaximized = win.state === WINDOW_STATE.MAXIMIZED;
  const isMinimized = win.state === WINDOW_STATE.MINIMIZED;

  const dragState = useRef(null);
  const snapRef = useRef(null);
  const [snapZone, setSnapZone] = useState(null);
  const [showSnapLayouts, setShowSnapLayouts] = useState(false);

  // --- Drag live state ---
  // Position during drag lives in local state — no Redux dispatches until mouseup.
  // Only this Window re-renders during drag; sibling windows are untouched.
  const [dragLive, setDragLive] = useState(null); // {x, y} | null
  const dragLiveRef = useRef(null);               // readable inside RAF closure
  const dragRafRef = useRef(null);                // pending animation frame id
  const dragPendingRef = useRef(null);            // last raw mouse event coords

  // --- Tear-off refs ---
  // tearoffFiredRef:    set to true by the fire handler so onUp knows it's a no-op.
  // tearoffPrearmedRef: true while the cursor is outside the Electron window boundary,
  //                     used to suppress moveWindow dispatch if fire is still in transit.
  // windowDivRef:       direct DOM ref — prearmed visual applied without a re-render.
  const tearoffFiredRef = useRef(false);
  const tearoffPrearmedRef = useRef(false);
  const windowDivRef = useRef(null);

  // --- Resize live state ---
  const [resizeLive, setResizeLive] = useState(null); // {x, y, w, h} | null
  const resizeLiveRef = useRef(null);

  const focus = useCallback(() => {
    if (!isFocused) dispatch(focusWindow(win.id));
  }, [isFocused, dispatch, win.id]);

  const applySnap = useCallback((zone) => {
    if (zone === "maximize") {
      dispatch(maximizeWindow(win.id));
      return;
    }
    const b = zoneBounds(zone, computeStage());
    if (!b) return;
    dispatch(restoreWindow(win.id));
    dispatch(setWindowBounds({ id: win.id, ...b }));
  }, [dispatch, win.id]);

  const handlePopout = useCallback(() => {
    const tdata = encodeURIComponent(
      JSON.stringify({ path: win.path, title: win.title, w: win.w, h: win.h })
    );
    if (window.electronAPI?.webtopTearoffCreate) {
      // Electron: create a native OS window centred on screen
      window.electronAPI.webtopTearoffCreate({
        winPath: win.path, title: win.title, w: win.w, h: win.h,
        cursorX: null, cursorY: null,
      });
    } else {
      // Web: open a new browser window with the same route
      window.open(
        `${window.location.origin}${window.location.pathname}?tearoff=1&tdata=${tdata}#/webtop`,
        "_blank",
        `width=${win.w || 960},height=${win.h || 640}`
      );
    }
    dispatch(closeWindow(win.id));
  }, [win.path, win.title, win.w, win.h, win.id, dispatch]);

  // ---- Drag (move) — no Redux dispatches per frame ----
  const onTitleMouseDown = useCallback((e) => {
    if (e.button !== 0 || isMaximized) return;
    console.log("[tearoff] titlebar mousedown, win:", win.id, "electronAPI:", !!window.electronAPI, "webtopDragStart:", !!window.electronAPI?.webtopDragStart);
    focus();
    const startX = e.clientX;
    const startY = e.clientY;
    const origX = win.x;
    const origY = win.y;
    const winW = win.w;
    dragState.current = "move";
    snapRef.current = null;

    // Compute stage dimensions ONCE at drag start — avoids layout reflow on every move.
    const stage = computeStage();

    const onMove = (ev) => {
      // Store latest coords; skip if a RAF is already queued.
      dragPendingRef.current = { x: ev.clientX, y: ev.clientY };
      if (dragRafRef.current) return;
      dragRafRef.current = requestAnimationFrame(() => {
        dragRafRef.current = null;
        const { x: cx, y: cy } = dragPendingRef.current;
        const maxX = window.innerWidth - 80;
        const minX = -(winW - 120);
        const nextX = Math.min(Math.max(origX + cx - startX, minX), maxX);
        const nextY = Math.min(Math.max(origY + cy - startY, 0), window.innerHeight - 60);
        // Suppress snap zones when Chromium reports coords outside the viewport.
        // This prevents "maximize" snap from stealing the gesture when the user is
        // dragging toward the top/side to exit to a second monitor.
        // Use exact boundary (no buffer) so snap suppression activates the moment
        // the cursor reaches the viewport edge — critical for detecting monitor 2.
        const outsideChromium =
          !!window.electronAPI?.webtopDragStart &&
          (cx < 0 || cy < 0 || cx >= window.innerWidth || cy >= window.innerHeight);
        const zone = outsideChromium ? null : detectZone(cx, cy, stage);
        snapRef.current = zone;
        dragLiveRef.current = { x: nextX, y: nextY };
        setDragLive({ x: nextX, y: nextY });
        setSnapZone(zone);
      });
    };

    // onUp is the NORMAL drag-end path (mouse released inside the app window).
    // It is a no-op if tearoffFiredRef is already set by the fire handler.
    const onUp = () => {
      window.electronAPI?.webtopDragEnd?.();
      windowDivRef.current?.classList.remove("webtop-window--tearoff-ready");

      // Guard: fire handler already handled this drag — clean up tearoff state and bail.
      // NOTE: do NOT call removeWebtopDragListeners before this check — the fire IPC
      // may still be in transit and would be silently dropped if we remove the listener first.
      if (tearoffFiredRef.current) {
        tearoffFiredRef.current = false;
        tearoffPrearmedRef.current = false;
        window.electronAPI?.removeWebtopDragListeners?.();
        return;
      }

      if (dragRafRef.current) { cancelAnimationFrame(dragRafRef.current); dragRafRef.current = null; }
      dragState.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const live = dragLiveRef.current;
      dragLiveRef.current = null;
      setDragLive(null);

      // Skip snap/move if the cursor was outside the Electron window (prearmed state).
      // A tearoff-fire IPC may still be in transit — dispatching moveWindow here would
      // flash the window to an edge position right before the fire handler closes it.
      if (!tearoffPrearmedRef.current) {
        if (snapRef.current) {
          applySnap(snapRef.current);
        } else if (live) {
          dispatch(moveWindow({ id: win.id, x: live.x, y: live.y }));
        }
      }
      tearoffPrearmedRef.current = false;
      snapRef.current = null;
      setSnapZone(null);

      // Defer tearoff listener removal by 100ms so any in-flight tearoff-fire IPC can
      // still arrive and be processed. The main process fires tearoff independently of
      // mouseup; both can happen within a few ms of each other.
      setTimeout(() => {
        if (!tearoffFiredRef.current) window.electronAPI?.removeWebtopDragListeners?.();
        tearoffFiredRef.current = false;
      }, 100);
    };

    // ── Electron tear-off: main process polls OS cursor during drag ───────────
    if (window.electronAPI?.webtopDragStart) {
      console.log("[tearoff] sending webtop-drag-start IPC for win:", win.id);
      window.electronAPI.webtopDragStart({
        id: win.id, path: win.path, title: win.title, w: win.w, h: win.h,
      });

      // Cursor just left the window → show dashed-border hint via DOM (no re-render)
      window.electronAPI.onWebtopTearoffPrearmed(() => {
        console.log("[tearoff] PREARMED received from main");
        tearoffPrearmedRef.current = true;
        windowDivRef.current?.classList.add("webtop-window--tearoff-ready");
      });

      // Cursor came back inside → remove hint
      window.electronAPI.onWebtopTearoffDisarmed(() => {
        tearoffPrearmedRef.current = false;
        windowDivRef.current?.classList.remove("webtop-window--tearoff-ready");
      });

      // Main process held outside for 150 ms → fire immediately (no mouseup needed)
      window.electronAPI.onWebtopTearoffFire((data) => {
        console.log("[tearoff] FIRE received from main, cursor:", data?.cursor, "win:", win.id);
        // Set the guard FIRST — before any cleanup — so a concurrently-queued onUp
        // sees it immediately and bails out rather than running normal drag-end logic.
        tearoffFiredRef.current = true;
        tearoffPrearmedRef.current = false;

        // Cancel the in-app drag
        if (dragRafRef.current) { cancelAnimationFrame(dragRafRef.current); dragRafRef.current = null; }
        dragState.current = null;
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
        dragLiveRef.current = null;
        setDragLive(null);
        setSnapZone(null);
        snapRef.current = null;
        windowDivRef.current?.classList.remove("webtop-window--tearoff-ready");
        window.electronAPI.removeWebtopDragListeners?.();

        // Remove window from main desktop and open it in a new OS window
        dispatch(closeWindow(win.id));
        window.electronAPI.webtopTearoffCreate({
          winPath: win.path,
          title: win.title,
          w: win.w,
          h: win.h,
          cursorX: data.cursor.x,
          cursorY: data.cursor.y,
        });
      });
    }

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [isMaximized, win.x, win.y, win.w, win.id, focus, applySnap, dispatch]);

  // ---- Resize — no Redux dispatches per frame ----
  const onResizeMouseDown = useCallback((dir) => (e) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    focus();
    const startX = e.clientX;
    const startY = e.clientY;
    const o = { x: win.x, y: win.y, w: win.w, h: win.h };
    dragState.current = "resize";
    // Local RAF state per gesture (not React refs — avoids stale-closure issues).
    const raf = { id: null, pending: null };

    const onMove = (ev) => {
      raf.pending = { x: ev.clientX, y: ev.clientY };
      if (raf.id) return;
      raf.id = requestAnimationFrame(() => {
        raf.id = null;
        const { x: cx, y: cy } = raf.pending;
        const dx = cx - startX, dy = cy - startY;
        let { x, y, w, h } = o;
        if (dir.includes("e")) w = Math.max(MIN_WIDTH, o.w + dx);
        if (dir.includes("s")) h = Math.max(MIN_HEIGHT, o.h + dy);
        if (dir.includes("w")) { w = Math.max(MIN_WIDTH, o.w - dx); x = o.x + (o.w - w); }
        if (dir.includes("n")) { h = Math.max(MIN_HEIGHT, o.h - dy); y = o.y + (o.h - h); }
        const bounds = { x, y, w, h };
        resizeLiveRef.current = bounds;
        setResizeLive(bounds);
      });
    };

    const onUp = () => {
      if (raf.id) { cancelAnimationFrame(raf.id); raf.id = null; }
      dragState.current = null;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      const live = resizeLiveRef.current;
      resizeLiveRef.current = null;
      setResizeLive(null);
      if (live) dispatch(setWindowBounds({ id: win.id, ...live }));
    };

    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [win.x, win.y, win.w, win.h, win.id, focus, dispatch]);

  // ---- Geometry — use live drag/resize coords; fall back to Redux state ----
  const liveX = dragLive?.x ?? resizeLive?.x ?? win.x;
  const liveY = dragLive?.y ?? resizeLive?.y ?? win.y;
  const liveW = resizeLive?.w ?? win.w;
  const liveH = resizeLive?.h ?? win.h;

  const style = isMaximized
    ? { left: 0, top: 0, right: 0, bottom: 0, zIndex: win.z }
    : { left: liveX, top: liveY, width: liveW, height: liveH, zIndex: win.z };

  if (isMinimized) style.display = "none";

  const className = [
    "webtop-window",
    isFocused ? "webtop-window--focused" : "",
    isMaximized ? "webtop-window--maximized" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const glyph = (win.title || win.appKey || "?").trim().charAt(0).toUpperCase();

  const appKeyOf = (p) => String(p || "").replace(/^\//, "").split(/[/?#]/)[0];
  const mergedCaption = appKeyOf(win.path) === "workspace-browser";

  // Memoize the context value — a new object every render would cause every
  // context consumer to re-render unnecessarily.
  const contextValue = useMemo(() => ({
    inWebtopWindow: true,
    windowId: win.id,
    startDrag: onTitleMouseDown,
    toggleMaximize: () => dispatch(toggleMaximize(win.id)),
  }), [onTitleMouseDown, dispatch, win.id]);

  const windowControls = (
    <div className="webtop-winbtns">
      <button
        type="button"
        className="webtop-winbtn"
        aria-label="Minimize"
        title="Minimize"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => dispatch(minimizeWindow(win.id))}
      >
        <MinIcon />
      </button>
      <div
        className="webtop-maxwrap"
        onMouseEnter={() => setShowSnapLayouts(true)}
        onMouseLeave={() => setShowSnapLayouts(false)}
      >
        <button
          type="button"
          className="webtop-winbtn"
          aria-label={isMaximized ? "Restore" : "Maximize"}
          title={isMaximized ? "Restore" : "Maximize"}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => dispatch(toggleMaximize(win.id))}
        >
          {isMaximized ? <RestoreIcon /> : <MaxIcon />}
        </button>
        {showSnapLayouts && (
          <div className="webtop-snaplayouts" role="menu" aria-label="Snap layouts">
            {SNAP_LAYOUTS.map((zone) => (
              <button
                key={zone}
                type="button"
                className="webtop-snaplayout"
                aria-label={`Snap ${zone}`}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={() => {
                  applySnap(zone);
                  setShowSnapLayouts(false);
                }}
              >
                <span className={`webtop-snaplayout__fill webtop-snaplayout__fill--${zone}`} />
              </button>
            ))}
          </div>
        )}
      </div>
      <button
        type="button"
        className="webtop-winbtn webtop-winbtn--popout"
        aria-label="Pop out to new window"
        title="Pop out to new window"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={handlePopout}
      >
        <PopoutIcon />
      </button>
      <button
        type="button"
        className="webtop-winbtn webtop-winbtn--close"
        aria-label="Close"
        title="Close"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={() => dispatch(closeWindow(win.id))}
      >
        <CloseIcon />
      </button>
    </div>
  );

  return (
    <WebtopWindowContext.Provider value={contextValue}>
      <div
        ref={windowDivRef}
        className={className}
        style={style}
        onMouseDown={focus}
        role="dialog"
        aria-label={win.title}
      >
        {mergedCaption ? (
          <div className="webtop-browser-caption">{windowControls}</div>
        ) : (
          <div
            className="webtop-titlebar"
            onMouseDown={onTitleMouseDown}
            onDoubleClick={() => dispatch(toggleMaximize(win.id))}
          >
            <span className="webtop-titlebar__icon" aria-hidden="true">{glyph}</span>
            <span className="webtop-titlebar__title">{win.title}</span>
            {canPin && (
              <button
                type="button"
                className={`webtop-winbtn webtop-pinbtn${isPinnedRecord ? " webtop-pinbtn--on" : ""}`}
                aria-label={isPinnedRecord ? "Remove desktop shortcut" : "Add to desktop"}
                title={isPinnedRecord ? "Remove desktop shortcut" : "Add to desktop"}
                onMouseDown={(e) => e.stopPropagation()}
                onClick={togglePinRecord}
              >
                <FontAwesomeIcon icon={faThumbtack} />
              </button>
            )}
            {windowControls}
          </div>
        )}

        <div className="webtop-content">
          <div className="webtop-content-scroll">{children}</div>
        </div>

        {!isMaximized &&
          RESIZE_DIRS.map((dir) => (
            <div
              key={dir}
              className={`webtop-resize webtop-resize--${dir}`}
              onMouseDown={onResizeMouseDown(dir)}
            />
          ))}

        {snapZone &&
          (() => {
            const b = zoneBounds(snapZone, computeStage());
            return b ? (
              <div
                className="webtop-snap-preview"
                style={{ left: b.x, top: b.y, width: b.w, height: b.h }}
              />
            ) : null;
          })()}
      </div>
    </WebtopWindowContext.Provider>
  );
// Re-render only when this window's own data changes — sibling window updates
// (focus, move, resize) produce new refs only for the window that changed.
}, (prev, next) => prev.win === next.win);

export default Window;
